/**
 * NEORISE Color Engine
 * ---------------------------------------------------------------------------
 * Real color-science primitives for the app: HEX <-> RGB <-> XYZ <-> CIELAB
 * conversion, a correct CIEDE2000 implementation, and a closest-match lookup
 * against the 150-color database with honest (non-fabricated) confidence
 * banding.
 *
 * This replaces the "ΔE2000" that was actually plain Euclidean Lab distance
 * in the original spec, and replaces the unrealistic "ΔE < 0.8" match
 * threshold with banded, user-facing labels.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RGB {
  r: number; // 0-255
  g: number; // 0-255
  b: number; // 0-255
}

export interface LAB {
  l: number; // 0-100
  a: number; // roughly -128 to 127
  b: number; // roughly -128 to 127
}

export interface ColorRecord {
  id: string;
  shadeCode: string;
  name: string;
  familyId: string;
  hex: string;
  lab: LAB;
}

export type MatchBand = "excellent" | "good" | "approximate" | "none";

export interface MatchResult {
  color: ColorRecord;
  deltaE: number;
  band: MatchBand;
  label: string;
}

// ---------------------------------------------------------------------------
// HEX <-> RGB
// ---------------------------------------------------------------------------

export function hexToRgb(hex: string): RGB {
  const clean = hex.replace("#", "").trim();
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) {
    throw new Error(`Invalid HEX color: "${hex}"`);
  }
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }: RGB): string {
  const toHex = (v: number) =>
    Math.round(clamp(v, 0, 255)).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

// ---------------------------------------------------------------------------
// RGB (sRGB) -> XYZ -> LAB
// ---------------------------------------------------------------------------

function srgbChannelToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function linearToSrgbChannel(v: number): number {
  const c = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  return clamp(c * 255, 0, 255);
}

// D65 reference white
const REF_X = 95.047;
const REF_Y = 100.0;
const REF_Z = 108.883;

export function rgbToXyz(rgb: RGB): { x: number; y: number; z: number } {
  const r = srgbChannelToLinear(rgb.r);
  const g = srgbChannelToLinear(rgb.g);
  const b = srgbChannelToLinear(rgb.b);

  return {
    x: (r * 0.4124 + g * 0.3576 + b * 0.1805) * 100,
    y: (r * 0.2126 + g * 0.7152 + b * 0.0722) * 100,
    z: (r * 0.0193 + g * 0.1192 + b * 0.9505) * 100,
  };
}

function xyzChannelToLab(t: number): number {
  const epsilon = 216 / 24389;
  const kappa = 24389 / 27;
  return t > epsilon ? Math.cbrt(t) : (kappa * t + 16) / 116;
}

export function xyzToLab(xyz: { x: number; y: number; z: number }): LAB {
  const fx = xyzChannelToLab(xyz.x / REF_X);
  const fy = xyzChannelToLab(xyz.y / REF_Y);
  const fz = xyzChannelToLab(xyz.z / REF_Z);

  return {
    l: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

export function rgbToLab(rgb: RGB): LAB {
  return xyzToLab(rgbToXyz(rgb));
}

export function hexToLab(hex: string): LAB {
  return rgbToLab(hexToRgb(hex));
}

// Inverse path (LAB -> XYZ -> RGB), useful for gamut-checking generated
// seed colors (see generate-seed-colors.ts).

function labChannelToXyz(t: number): number {
  const epsilon = 216 / 24389;
  const kappa = 24389 / 27;
  const t3 = t * t * t;
  return t3 > epsilon ? t3 : (116 * t - 16) / kappa;
}

export function labToXyz(lab: LAB): { x: number; y: number; z: number } {
  const fy = (lab.l + 16) / 116;
  const fx = fy + lab.a / 500;
  const fz = fy - lab.b / 200;

  return {
    x: labChannelToXyz(fx) * REF_X,
    y: labChannelToXyz(fy) * REF_Y,
    z: labChannelToXyz(fz) * REF_Z,
  };
}

export function xyzToRgb(xyz: { x: number; y: number; z: number }): RGB {
  const x = xyz.x / 100;
  const y = xyz.y / 100;
  const z = xyz.z / 100;

  const r = x * 3.2406 + y * -1.5372 + z * -0.4986;
  const g = x * -0.9689 + y * 1.8758 + z * 0.0415;
  const b = x * 0.0557 + y * -0.204 + z * 1.057;

  return {
    r: linearToSrgbChannel(r),
    g: linearToSrgbChannel(g),
    b: linearToSrgbChannel(b),
  };
}

export function labToRgb(lab: LAB): RGB {
  return xyzToRgb(labToXyz(lab));
}

/**
 * Returns true if a Lab color round-trips into displayable sRGB without
 * clipping — i.e. it corresponds to a real, displayable color. Used to
 * reject out-of-gamut candidates when generating the 150-color seed set.
 */
export function isInSrgbGamut(lab: LAB, tolerance = 0.5): boolean {
  const xyz = labToXyz(lab);
  const x = xyz.x / 100;
  const y = xyz.y / 100;
  const z = xyz.z / 100;

  const rawR = x * 3.2406 + y * -1.5372 + z * -0.4986;
  const rawG = x * -0.9689 + y * 1.8758 + z * 0.0415;
  const rawB = x * 0.0557 + y * -0.204 + z * 1.057;

  const inRange = (v: number) => v >= -tolerance / 255 && v <= 1 + tolerance / 255;
  return inRange(rawR) && inRange(rawG) && inRange(rawB);
}

// ---------------------------------------------------------------------------
// CIEDE2000 — the actual perceptual color-difference formula.
// This is the piece the original spec mislabeled (it specified plain
// Euclidean Lab distance and called it "ΔE2000").
// ---------------------------------------------------------------------------

export function deltaE2000(lab1: LAB, lab2: LAB): number {
  const { l: L1, a: a1, b: b1 } = lab1;
  const { l: L2, a: a2, b: b2 } = lab2;

  const kL = 1;
  const kC = 1;
  const kH = 1;

  const C1 = Math.sqrt(a1 * a1 + b1 * b1);
  const C2 = Math.sqrt(a2 * a2 + b2 * b2);
  const CBar = (C1 + C2) / 2;

  const CBar7 = Math.pow(CBar, 7);
  const G = 0.5 * (1 - Math.sqrt(CBar7 / (CBar7 + Math.pow(25, 7))));

  const a1p = a1 * (1 + G);
  const a2p = a2 * (1 + G);

  const C1p = Math.sqrt(a1p * a1p + b1 * b1);
  const C2p = Math.sqrt(a2p * a2p + b2 * b2);

  const h1p = hueAngleDeg(b1, a1p);
  const h2p = hueAngleDeg(b2, a2p);

  const deltaLp = L2 - L1;
  const deltaCp = C2p - C1p;

  let deltahp = 0;
  if (C1p * C2p !== 0) {
    const diff = h2p - h1p;
    if (Math.abs(diff) <= 180) deltahp = diff;
    else if (diff > 180) deltahp = diff - 360;
    else deltahp = diff + 360;
  }
  const deltaHp =
    2 * Math.sqrt(C1p * C2p) * Math.sin(degToRad(deltahp) / 2);

  const LBarp = (L1 + L2) / 2;
  const CBarp = (C1p + C2p) / 2;

  let HBarp: number;
  if (C1p * C2p === 0) {
    HBarp = h1p + h2p;
  } else {
    const diff = Math.abs(h1p - h2p);
    if (diff <= 180) HBarp = (h1p + h2p) / 2;
    else if (h1p + h2p < 360) HBarp = (h1p + h2p + 360) / 2;
    else HBarp = (h1p + h2p - 360) / 2;
  }

  const T =
    1 -
    0.17 * Math.cos(degToRad(HBarp - 30)) +
    0.24 * Math.cos(degToRad(2 * HBarp)) +
    0.32 * Math.cos(degToRad(3 * HBarp + 6)) -
    0.2 * Math.cos(degToRad(4 * HBarp - 63));

  const deltaTheta =
    30 * Math.exp(-Math.pow((HBarp - 275) / 25, 2));

  const CBarp7 = Math.pow(CBarp, 7);
  const RC = 2 * Math.sqrt(CBarp7 / (CBarp7 + Math.pow(25, 7)));

  const SL =
    1 + (0.015 * Math.pow(LBarp - 50, 2)) / Math.sqrt(20 + Math.pow(LBarp - 50, 2));
  const SC = 1 + 0.045 * CBarp;
  const SH = 1 + 0.015 * CBarp * T;

  const RT = -Math.sin(degToRad(2 * deltaTheta)) * RC;

  const termL = deltaLp / (kL * SL);
  const termC = deltaCp / (kC * SC);
  const termH = deltaHp / (kH * SH);

  const deltaE = Math.sqrt(
    termL * termL + termC * termC + termH * termH + RT * termC * termH
  );

  return deltaE;
}

function hueAngleDeg(b: number, aPrime: number): number {
  if (b === 0 && aPrime === 0) return 0;
  let angle = Math.atan2(b, aPrime) * (180 / Math.PI);
  if (angle < 0) angle += 360;
  return angle;
}

/** Chroma (colorfulness) of a Lab color: sqrt(a^2 + b^2). */
export function chromaOf(lab: LAB): number {
  return Math.sqrt(lab.a * lab.a + lab.b * lab.b);
}

/** Hue angle in degrees [0, 360) of a Lab color. */
export function hueAngleOf(lab: LAB): number {
  return hueAngleDeg(lab.b, lab.a);
}

/** Builds a Lab color from lightness, hue angle (degrees), and chroma. */
export function labFromLch(l: number, hueDeg: number, chroma: number): LAB {
  const rad = (hueDeg * Math.PI) / 180;
  return {
    l,
    a: chroma * Math.cos(rad),
    b: chroma * Math.sin(rad),
  };
}

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

// ---------------------------------------------------------------------------
// Closest-match lookup with honest confidence banding
// (replaces the fabricated "96% accuracy" style output)
// ---------------------------------------------------------------------------

export function bandForDeltaE(deltaE: number): { band: MatchBand; label: string } {
  if (deltaE <= 2) return { band: "excellent", label: "Excellent match" };
  if (deltaE <= 5) return { band: "good", label: "Good match" };
  if (deltaE <= 10)
    return {
      band: "approximate",
      label: "Approximate match — verify in person before ordering",
    };
  return { band: "none", label: "No close match found" };
}

/**
 * Cheap pre-filter using plain Lab Euclidean distance (fast, index-friendly —
 * this is what the SQL layer should do to narrow ~150 rows down to a small
 * candidate set). Not perceptually accurate on its own, hence the second
 * pass below.
 */
export function labEuclideanDistance(a: LAB, b: LAB): number {
  return Math.sqrt(
    Math.pow(a.l - b.l, 2) + Math.pow(a.a - b.a, 2) + Math.pow(a.b - b.b, 2)
  );
}

/**
 * Full closest-match pipeline:
 *   1. Cheap Euclidean pre-filter to top N candidates (mirrors what the
 *      SQL pre-filter query would return).
 *   2. Exact CIEDE2000 ranking over just those candidates.
 *   3. Honest confidence banding — no fabricated percentages.
 */
export function findClosestMatches(
  target: LAB,
  database: ColorRecord[],
  options: { preFilterCount?: number; topN?: number } = {}
): MatchResult[] {
  const preFilterCount = options.preFilterCount ?? 20;
  const topN = options.topN ?? 3;

  const preFiltered = [...database]
    .sort(
      (a, b) =>
        labEuclideanDistance(target, a.lab) - labEuclideanDistance(target, b.lab)
    )
    .slice(0, preFilterCount);

  const ranked = preFiltered
    .map((color) => {
      const deltaE = deltaE2000(target, color.lab);
      const { band, label } = bandForDeltaE(deltaE);
      return { color, deltaE, band, label };
    })
    .sort((a, b) => a.deltaE - b.deltaE);

  return ranked.slice(0, topN);
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}
